package horcrux.frontend.common.views.collections.importdocuments

import arrow.core.getOrElse
import arrow.core.recover
import arrow.core.toOption
import com.benasher44.uuid.uuid4
import horcrux.common.api.dtos.RawDocumentClientSideID
import horcrux.common.api.dtos.types.DocumentInput
import horcrux.common.api.dtos.types.DocumentName
import horcrux.common.extensions.last
import horcrux.common.extensions.nonEmptySortedBy
import horcrux.common.utils.toOptional
import horcrux.frontend.common.BooleanState
import horcrux.frontend.common.DocumentInputData
import horcrux.frontend.common.DocumentInputDataByClientSideIDState
import horcrux.frontend.common.GlobalAppState
import horcrux.frontend.common.MaybeState
import horcrux.frontend.common.extensions.marginVertical
import horcrux.frontend.common.io
import horcrux.frontend.common.value
import horcrux.frontend.common.views.components.containers.DialogAction
import horcrux.frontend.common.views.components.containers.DialogView
import horcrux.frontend.common.views.components.containers.HStack
import horcrux.frontend.common.views.components.containers.VStack
import horcrux.frontend.common.views.components.input.FileInput
import horcrux.frontend.common.views.components.input.NewWindowLink
import js.objects.jso
import mui.icons.material.Close
import mui.icons.material.Edit
import mui.lab.LoadingButton
import mui.material.AlertColor
import mui.material.DialogContent
import mui.material.DialogContentText
import mui.material.DialogTitle
import mui.material.FormControlMargin
import mui.material.FormControlVariant
import mui.material.IconButton
import mui.material.TextField
import mui.material.Typography
import mui.material.styles.TypographyVariant
import mui.system.PropsWithSx
import mui.system.sx
import react.FC
import react.Props
import react.ReactNode
import react.dom.html.ReactHTML.p
import react.dom.onChange
import web.cssom.AlignItems
import web.cssom.JustifyContent
import web.cssom.Overflow
import web.cssom.TextAlign
import web.cssom.WhiteSpace
import web.cssom.WordBreak
import web.cssom.pct
import web.cssom.pt
import web.html.InputType

private external interface DocumentInputViewProps : PropsWithSx {
    var rawDocumentClientSideID: RawDocumentClientSideID
    var documentInputDataByClientSideIDState: DocumentInputDataByClientSideIDState
}

private val DocumentInputView = FC<DocumentInputViewProps> { props ->
    val documentInputDataByClientSideID = props.documentInputDataByClientSideIDState.get()
    val documentInputData = documentInputDataByClientSideID.unsafeGet(props.rawDocumentClientSideID)
    val updateDocumentInputsMaybeState = { documentInputMaybe: DocumentInputMaybe<out DocumentInput> ->
        props.documentInputDataByClientSideIDState.update { documentInputDataByClientSideID ->
            val newDocumentInputData = documentInputData.withDocumentInputMaybe(documentInputMaybe)
            documentInputDataByClientSideID.replace(props.rawDocumentClientSideID, newDocumentInputData)
        }
    }
    documentInputData.rawDocumentMetadataMaybe?.let {
        Typography {
            +documentInputData.documentName.betterToString()
            this.key = "${documentInputData.documentName.betterToString()}-${uuid4()}"
            this.paragraph = true
            this.variant = TypographyVariant.caption
            this.sx {
                this.maxWidth = 75.pct
                this.whiteSpace = WhiteSpace.normal
                this.overflow = Overflow.visible
                this.wordBreak = WordBreak.breakWord
                this.textAlign = TextAlign.left
            }
        }
    } ?: run {
        when (val documentInputMaybe = documentInputData.documentInputMaybe) {
            is DocumentURLMaybe -> {
                val documentURLMaybe = documentInputMaybe.valueMaybe
                TextField {
                    documentURLMaybe?.betterToString()?.let { this.defaultValue = it }
                    this.autoFocus = true
                    this.fullWidth = true
                    this.error = documentInputData.isInvalid
                    this.helperText = ReactNode(documentInputData.errorMessage)
                    this.id = "input-document-url"
                    this.label = ReactNode("Document URL")
                    this.margin = FormControlMargin.dense
                    this.onChange = { event ->
                        updateDocumentInputsMaybeState(DocumentURLMaybe(event.value()))
                    }
                    this.sx { +props.sx }
                    this.type = InputType.url
                    this.variant = FormControlVariant.standard
                }
            }

            is DocumentByteArrayMaybe -> {
                p { +documentInputMaybe.valueMaybe?.rawDocumentName?.betterToString() }
            }

            is DocumentFileMaybe -> {
                FileInput {
                    this.onFileChange = { file ->
                        updateDocumentInputsMaybeState(DocumentFileMaybe(file))
                    }
                    if (documentInputData.isInvalid) {
                        this.errorTextMaybe = documentInputData.errorMessage
                    }
                    this.inputProps = jso {
                        this.sx { +props.sx }
                    }
                }
            }

            is DocumentTextMaybe -> {
                TextField {
                    this.autoFocus = true
                    this.error = documentInputData.isInvalid
                    this.helperText = ReactNode(documentInputData.errorMessage)
                    this.fullWidth = true
                    this.id = "input-document-selection"
                    this.label = ReactNode("Document Text")
                    this.margin = FormControlMargin.dense
                    this.maxRows = 10
                    this.minRows = 5
                    this.multiline = true
                    this.onChange = { event ->
                        updateDocumentInputsMaybeState(DocumentTextMaybe(event.value()))
                    }
                    this.sx { +props.sx }
                    this.value = documentInputMaybe.valueMaybe?.betterToString()
                    this.variant = FormControlVariant.standard
                }
            }
        }
    }
}

private external interface EditDocumentDetailsDialogProps : Props {
    var rawDocumentClientSideID: RawDocumentClientSideID
    var documentInputDataByClientSideIDState: DocumentInputDataByClientSideIDState
    var globalAppState: GlobalAppState
}

private val EditDocumentDetailsDialogProps.documentInputData: DocumentInputData
    get() {
        val documentInputDataByClientSideID = this.documentInputDataByClientSideIDState.get()
        return documentInputDataByClientSideID.unsafeGet(this.rawDocumentClientSideID)
    }

private val EditDocumentDetailsDialogProps.documentNameMaybe: DocumentName?
    get() = documentInputData.documentName

private fun EditDocumentDetailsDialogProps.updateDocumentInputData(documentInputData: DocumentInputData) =
    documentInputDataByClientSideIDState.update {
        it.replace(rawDocumentClientSideID, documentInputData)
    }

private fun EditDocumentDetailsDialogProps.updateDocumentName(documentName: DocumentName) =
    updateDocumentInputData(documentInputData.copy(documentName = documentName))

private fun EditDocumentDetailsDialogProps.reset() =
    updateDocumentInputData(documentInputData.copy(documentName = documentInputData.documentInputMaybe.defaultDocumentName))


private val EditDocumentDetailsDialog = FC<EditDocumentDetailsDialogProps> { props ->
    val showDialogState = BooleanState()
    val documentNameMaybeState = MaybeState<DocumentName>()
    val title = "Edit Document Details"
    val documentNameIsInvalid = documentNameMaybeState.get().toOption().map { it.isInvalid() }
        .recover { props.documentNameMaybe?.isInvalid() ?: false }.getOrElse { false }
    val isInvalid = {
        documentNameIsInvalid
    }
    val reset = {
        documentNameMaybeState.reset()
    }

    IconButton {
        this.onClick = {
            reset()
            props.documentInputData.documentInputMaybe.valueMaybe?.let {
                showDialogState.setTrue()
            } ?: run {
                props.globalAppState.updateBanner(AlertColor.error, "Please select a document")
            }
        }
        Edit {}
    }

    DialogView {
        this.title = title
        this.showDialogState = showDialogState
        this.dialogActionsMaybe =
            listOf(DialogAction("Reset") { _, _ -> reset() }, DialogAction("OK") { _, _ ->
                if (!isInvalid()) {
                    documentNameMaybeState.get()?.let { props.updateDocumentName(it) }
                    showDialogState.setFalse()
                } else {
                    props.globalAppState.updateBanner(AlertColor.error, "Please make sure all inputs are valid")
                }
            })
        DialogTitle { +title }
        DialogContent {
            DialogContentText { +"Edit Document Name" }
            EditDocumentName {
                this.textFieldValueMaybeState = documentNameMaybeState
                this.defaultValueMaybe = props.documentNameMaybe?.betterToString()
                this.textFieldPropsMaybe = jso {
                    this.error = documentNameIsInvalid
                    this.helperText = documentNameIsInvalid.toOptional()?.let { ReactNode("Document name is invalid") }
                    this.sx {
                        this.width = 300.pt
                    }
                }
            }
            props.documentInputData.rawDocumentMetadataMaybe?.let { rawDocumentMetadata ->
                DialogContentText {
                    +"Document Type"
                    this.sx {
                        this.marginTop = 15.pt
                    }
                }
                p { +rawDocumentMetadata.documentType.displayName }
                NewWindowLink {
                    this.url = rawDocumentMetadata.documentURL.betterToString()
                    this.linkProps = jso {
                        +"Uploaded Raw Document URL"
                        this.sx {
                            this.marginTop = 15.pt
                        }
                    }
                }
            } ?: run {
                p { +"This document has not been uploaded" }
            }
        }
    }
}

external interface DocumentInputsProps : Props {
    var documentInputDataByClientSideIDState: DocumentInputDataByClientSideIDState
    var globalAppState: GlobalAppState
}

val DocumentInputs = FC<DocumentInputsProps> { props ->
    val documentInputDataByClientSideID = props.documentInputDataByClientSideIDState.get()
    val isLoadingState = BooleanState()
    VStack {
        this.sx {
            this.width = 100.pct
        }
        documentInputDataByClientSideID
            .map { it.key }
            .sorted()
            .forEach { rawDocumentClientSideID ->
                HStack {
                    this.sx {
                        this.width = 100.pct
                        this.marginVertical = 5.pt
                        this.alignItems = AlignItems.center
                        this.justifyContent = JustifyContent.center
                    }
                    DocumentInputView {
                        this.rawDocumentClientSideID = rawDocumentClientSideID
                        this.documentInputDataByClientSideIDState = props.documentInputDataByClientSideIDState
                    }
                    EditDocumentDetailsDialog {
                        this.rawDocumentClientSideID = rawDocumentClientSideID
                        this.documentInputDataByClientSideIDState = props.documentInputDataByClientSideIDState
                        this.globalAppState = props.globalAppState
                    }
                    IconButton {
                        Close {}
                        this.onClick = {
                            documentInputDataByClientSideID
                                .safeRemove(rawDocumentClientSideID)
                                .onRight { props.documentInputDataByClientSideIDState.set(it) }
                                .onLeft {
                                    props.globalAppState.updateBanner(
                                        AlertColor.error,
                                        "You must import at least one document"
                                    )
                                }
                        }
                    }
                }
            }
        LoadingButton {
            +"Add Another Document"
            this.loading = isLoadingState.get()
            this.onClick = {
                io {
                    props.globalAppState.uploadRawDocumentAndAddNewDocumentInput(
                        props.documentInputDataByClientSideIDState.get().last().second,
                        isLoadingState
                    ).bind()
                }
            }
        }
    }
}

